import { formatCurrency } from '../../../../utils/formatters.util';
import React, { forwardRef, memo, useImperativeHandle, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Checkbox } from '../../../../components/tailwind';
import ItemPricing from './ItemPricing';
import ProductAvailability from '../../../../components/tailwind/ProductAvailability';
import PersonalizationSummary from './PersonalizationSummary';
import { Views } from '@types';
import TermsAndConditions from './TermsAndConditions';
import UpdateVariant from './UpdateVariant';
import ActiveVariant from './ActiveVariant';
import PersonalizationSummaryModal, { PersonalizationSummaryModalRef } from './PersonalizationSummaryModal';
import { FaChevronLeft } from 'react-icons/fa';
import QuantityInput from '@components/tailwind/QuantityInput';
import { Markdown } from '@features/markdown/components/Markdown';
import cx from '@utils/cx.util';

type Props = {
  config: any;
  faces: any;
  graphicType: any;
  printType: any;
  view: string;
  setView: (view: string) => void;
  action: any;
  elements: any;
  hasElements: boolean;
  processing: boolean;
  canExport: boolean;
  submit: any;
  addFace: any;
  selectElement: (faceName: string, elementId: number) => void;
  removeElement: (faceName: string, elementId: number) => void;
  setActiveGroup: (index: number) => void;
  showReview: boolean;
  promoCodeId?: number;
  lineId?: string;
  quantity: number;
  setQuantity: React.Dispatch<React.SetStateAction<number>>;
};

export type ItemInfoForwardedRefs = {
  setView;
  getTotalPrice: () => string;
};

const ItemInfo: React.ForwardRefRenderFunction<ItemInfoForwardedRefs, Props> = (
  {
    config,
    faces,
    graphicType,
    printType,
    view,
    setView,
    action,
    elements,
    hasElements,
    processing,
    canExport,
    submit,
    addFace,
    selectElement,
    removeElement,
    setActiveGroup,
    showReview,
    promoCodeId,
    lineId,
    quantity: qty,
    setQuantity,
  },
  forwardedRef
) => {
  const [acceptTerms, setAcceptTerms] = useState(true);
  const summaryModalRef = useRef<PersonalizationSummaryModalRef>(null);

  const showVariant = !promoCodeId;

  const { details, site } = useSelector((state: any) => state.pdpItem);

  const quantityRef = useRef(null);

  if (!config) return null;

  const group = elements.find((e) => !!e.isGroup);
  const hasGroup = !!group;
  const groupCount = group?.data?.length;
  const quantity = (hasGroup ? groupCount : qty) || 1;
  const [localQuantity, setLocalQuantity] = useState(quantity);

  const showQuantity = !hasGroup && !promoCodeId;

  const basePrice = !promoCodeId ? details?.rate : 0;

  const flags = site?.flags;
  const category = config?.[0]?.category;
  const processingDays =
    (flags?.addCustomizationDays || []).find(
      (c) => c.processingType === 'processing' && c.category === category && c.graphicType === graphicType
    )?.days ?? 0;
  const rushDays =
    (flags?.addCustomizationDays || []).find(
      (c) => c.processingType === 'rush' && c.category === category && c.graphicType === graphicType
    )?.days ?? 0;

  const isRushEnabled = !!site?.flags?.enableCustomizationRush && processingDays !== rushDays;

  const facePrices = faces.reduce((a, f) => {
    const faceConfig = config.find((c) => c.face === f.key);
    const facePrice = Math.min(
      ...(faceConfig?.miyoFees || [])
        .filter((f) => f.graphicType === graphicType && f.feeKey === 'face')
        .map((f) => f.rate)
    );

    a[f.key] = facePrice;
    return a;
  }, {});
  const facePrice: any = Object.values(facePrices).reduce((a: number, p: number) => a + p, 0);

  const faceRates = (config[0]?.miyoFees || [])
    .filter((f) => f.graphicType === graphicType && f.feeKey === 'file')
    .map((f) => f.rate);
  const elementPrice = faceRates.length > 0 ? Math.min(...faceRates) : 0;

  const elementsToCharge: any = Object.values(
    (elements ?? []).reduce((a, e) => {
      if (e.elementType === 'uploaded' || e.elementType === 'file') {
        a[e.fileId] = e;
      }
      return a;
    }, {})
  );

  const premiumElements: any = Object.values(
    (faces ?? []).reduce(
      (a, f) =>
        f.elements.reduce((a, e) => {
          if (!!e.fee) {
            if (!a[e.fee.id]) {
              a[e.fee.id] = Object.assign({ quantity: 1 }, e.fee);
            } else {
              a[e.fee.id].quantity++;
            }
          }
          return a;
        }, a),
      {}
    )
  );
  const premiumPrice = premiumElements.reduce((a, p) => a + p.rate * p.quantity, 0);

  const personalizationPrice = parseFloat(
    (facePrice + elementsToCharge.length * elementPrice + premiumPrice).toFixed(2)
  );
  const price = parseFloat((basePrice + personalizationPrice).toFixed(2));
  const subtotal = price * quantity;

  const onAddPersonalizationClick = (config) => () => {
    setView(Views.DESIGN);
    addFace(config);
  };

  const onEditElement = (faceName, elementId) => () => {
    setView(Views.DESIGN);
    selectElement(faceName, elementId);
  };

  const onRemoveElement = (faceName, elementId) => () => {
    setView(Views.REVIEW_UPDATE_PREVIEW);
    removeElement(faceName, elementId);
  };

  const onSetActiveGroup = (index) => () => {
    // setView(Views.REVIEW_UPDATE_PREVIEW);
    setActiveGroup(index);
  };

  useImperativeHandle(
    forwardedRef,
    () => ({
      setView,
      getTotalPrice: () => formatCurrency(subtotal),
    }),
    [setView, subtotal]
  );

  const soldOut = details?.stock === 'soldOut';

  return (
    <div>
      {view === Views.UPDATE_VARIANT && <UpdateVariant setView={setView} />}

      {(view === Views.PERSONALIZATION || view === Views.REVIEW) && (
        <PersonalizationSummary
          {...{
            quantity,
            onRemoveElement,
            onEditElement,
            onSetActiveGroup,
            config,
            facePrices,
            elementPrice,
            faces,
            onAddPersonalizationClick,
          }}
          onSummaryModalOpen={() => summaryModalRef.current?.openModal()}
          title={view === Views.PERSONALIZATION ? 'Personalization Fees' : 'Design Summary'}
        />
      )}

      <PersonalizationSummaryModal
        ref={summaryModalRef}
        {...{
          quantity,
          onRemoveElement,
          onEditElement,
          onSetActiveGroup,
          config,
          facePrices,
          elementPrice,
          faces,
          onAddPersonalizationClick,
          personalizationPrice,
          processingDays,
          premiumElements,
          elementsToCharge,
          setView,
          price,
          quantityRef,
          canExport,
          acceptTerms,
          setAcceptTerms,
        }}
        addToCartText={action === 'update' || !!lineId ? 'Update Cart' : 'Add To Cart'}
        onAddToCartSubmit={submit}
        isAddToCartDisabled={!canExport || processing || !hasElements || !acceptTerms || soldOut}
        title={
          <div
            className='tw-inline-flex tw-items-center tw-gap-2'
            onClick={() => {
              summaryModalRef.current?.closeModal();
            }}>
            <FaChevronLeft size={14} />
            <p className='tw-mb-0 tw-text-xs tw-font-bold tw-text-brand-black lg:tw-text-base'>
              Review Names
            </p>
          </div>
        }
      />

      {![Views.TERMS, Views.UPDATE_VARIANT].includes(view as Views) && (
        <>
          {showVariant && (
            <ActiveVariant
              onUpdateClick={() => setView(Views.UPDATE_VARIANT)}
              view={view as Views}
              quantity={quantity}
            />
          )}

          <div className='tw-mb-4 tw-w-full tw-border-b tw-border-gray-300'>
            <ItemPricing
              personalizationFees={personalizationPrice}
              personalizationBreakdown={getPersonalizationBreakdown(
                facePrices,
                elementPrice,
                elementsToCharge,
                premiumElements
              )}
              {...{ quantity }}
            />
          </div>

          <div className='tw-flex tw-items-center tw-justify-between tw-gap-2'>
            <div className='tw-max-w-[230px]'>
              <ProductAvailability
                quantityPerOrderLimit={details?.quantityPerOrderLimit}
                stock={details?.stock}
                processingDays={processingDays}
              />
            </div>
            {subtotal > 0 && (
              <p className='tw-mb-0 tw-inline-block tw-text-[28px] tw-font-bold tw-text-brand-black'>
                {formatCurrency(subtotal)}
              </p>
            )}
          </div>
        </>
      )}

      {view === Views.PERSONALIZATION && (
        <div className='tw-mt-10 tw-text-center'>
          <Button type='button' variant='primary' onClick={() => setView(Views.REVIEW)}>
            Review Your Design
          </Button>
        </div>
      )}

      {view === Views.REVIEW && (
        <div className='tw-pb-[150px] lg:tw-pb-0'>
          <div className='tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-30 tw-w-screen tw-bg-white tw-px-4 tw-pt-2 tw-text-center sm:tw-relative sm:-tw-left-4 sm:tw-mt-10 sm:tw-w-[calc(100%+32px)] sm:tw-rounded-b-[30px] sm:tw-bg-transparent sm:tw-bg-white sm:tw-p-3 lg:-tw-left-10 lg:-tw-mb-8 lg:tw-w-[calc(100%+80px)]'>
            {isRushEnabled && (
              <div className='-tw-mx-4 -tw-mt-2 tw-mb-3 tw-border-b tw-border-b-gray-300 tw-text-center tw-text-xs md:-tw-mx-3 md:-tw-mt-3'>
                <div className='tw-bg-[#e6e7ea] tw-px-4 tw-py-2'>
                  {site.flags.enableCustomizationRush[0].moduleMessaging && (
                    <Markdown
                      source={site.flags.enableCustomizationRush[0].moduleMessaging}
                      placeholders={{
                        MyRTIC: () => (
                          <img
                            src='/assets/images/rtic/myRTIC-Logo.png'
                            alt='MyRTIC'
                            className='tw-inline-block tw-h-[14px] tw-max-w-[65px] tw-align-middle'
                          />
                        ),
                        processingDays: () => `${processingDays} business day${processingDays > 1 && 's'}`,
                      }}
                    />
                  )}
                </div>
              </div>
            )}
            <span className='tw-mx-auto tw-block tw-max-w-[280px] tw-text-[10px] tw-font-normal tw-text-neutrals-medium-grey'>
              By proceeding with personalization, you are agreeing to{' '}
              <span
                className='tw-cursor-pointer tw-text-brand-black tw-underline tw-transition tw-duration-200 hover:tw-opacity-75'
                onClick={() => setView('terms-and-conditions')}>
                MyRTIC Terms & Conditions.
              </span>
            </span>

            <div className='tw-mt-4 tw-flex tw-items-center tw-justify-between tw-gap-2'>
              {showQuantity && (
                <div className='tw-max-w-[100px]'>
                  <QuantityInput
                    ref={quantityRef}
                    decreaseHandler={() =>
                      +localQuantity > 1 &&
                      (setLocalQuantity(+localQuantity - 1), setQuantity(+localQuantity - 1))
                    }
                    increaseHandler={() => (
                      setLocalQuantity(+localQuantity + 1), setQuantity(+localQuantity + 1)
                    )}
                    inputId='quantity'
                    onChange={(e) => setLocalQuantity(+e.target.value)}
                    onBlur={() => setQuantity(localQuantity)}
                    value={localQuantity}
                    minQuantity={1}
                  />
                </div>
              )}

              <Button
                disabled={!canExport || processing || !hasElements || !acceptTerms || soldOut}
                variant='primary'
                fullWidth='all'
                onClick={submit}
                noPadding
                className='tw-h-[44px] tw-px-6 tw-py-3 tw-text-base'>
                {action === 'update' || !!lineId ? (
                  <>UPDATE CART</>
                ) : (
                  <span className='tw-whitespace-nowrap'>ADD TO CART</span>
                )}
              </Button>
            </div>

            <Button variant='link' noPadding className='tw-p-2' onClick={() => setView('design')}>
              CANCEL
            </Button>

            {!canExport && (
              <div className='text-danger text-center text-small tw-mb-1 tw-mt-1'>
                There was a problem generating the required files.
              </div>
            )}
          </div>
        </div>
      )}

      {view === Views.TERMS && <TermsAndConditions setView={setView} />}

      {view === Views.DESIGN && (
        <>
          <div className={cx({ 'tw-h-[75px]': !isRushEnabled })}>
            <div
              className={cx(
                'tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-30 tw-w-screen tw-bg-white tw-px-4 tw-text-center tw-shadow-[0px_-3px_15px_rgba(0,0,0,0.2)] sm:tw-relative sm:tw-mt-5 sm:tw-w-auto sm:tw-bg-transparent sm:tw-shadow-none',
                {
                  'tw-py-4': !isRushEnabled,
                  'tw-pb-4': isRushEnabled && hasElements && showReview,
                  'lg:-tw-mb-8': isRushEnabled && !(hasElements && showReview),
                }
              )}>
              {isRushEnabled && (
                <div
                  className={cx('-tw-mx-8 tw-text-center tw-text-xs lg:-tw-mx-14', {
                    'tw-mb-3': hasElements && showReview,
                  })}>
                  <div
                    className={cx('tw-bg-[#e6e7ea] tw-px-4 tw-py-2', {
                      'lg:tw-rounded-b-xl': !(hasElements && showReview),
                    })}>
                    {site.flags.enableCustomizationRush[0].moduleMessaging && (
                      <Markdown
                        source={site.flags.enableCustomizationRush[0].moduleMessaging}
                        placeholders={{
                          MyRTIC: () => (
                            <img
                              src='/assets/images/rtic/myRTIC-Logo.png'
                              alt='MyRTIC'
                              className='tw-inline-block tw-h-[14px] tw-max-w-[65px] tw-align-middle'
                            />
                          ),
                          processingDays: () => `${processingDays} business day${processingDays > 1 && 's'}`,
                        }}
                      />
                    )}
                  </div>
                </div>
              )}

              {hasElements && showReview && (
                <Button
                  variant='primary'
                  onClick={() => setView(Views.REVIEW)}
                  fullWidth='all'
                  size='lg'
                  type='button'>
                  Review Design
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default memo(forwardRef(ItemInfo));

const numberToLettersMap = {
  1: 'Personalized',
  2: 'Front & Back',
};

export const getPersonalizationBreakdown = (facePrices, elementPrice, elementsToCharge, premiumElements) => {
  const breakdownLines = [];
  const facesCount = Object.keys(facePrices).length;
  const facePricesTotal = Object.values(facePrices).reduce((acc: number, val: number) => acc + val, 0);

  facesCount &&
    breakdownLines.push({
      text: `${numberToLettersMap[facesCount]}`,
      value: facePricesTotal,
    });

  for (const element of elementsToCharge) {
    breakdownLines.push({
      text: 'Uploaded Artwork Added',
      value: elementPrice,
    });
  }
  for (const element of premiumElements) {
    breakdownLines.push({
      text: element.title,
      value: element.rate,
    });
  }

  return breakdownLines;
};
